import React, { useState } from 'react';
import { companyInfo } from '../data';
import { Send, Phone, Mail, MapPin, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';

export function FormContent({ status, setStatus, handleSubmit }) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-[2.5rem] p-8 md:p-12 text-slate-900 shadow-2xl"
    >
      {status === 'success' ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={48} />
          </div>
          <h4 className="text-3xl font-black mb-4 uppercase">Tak for din booking!</h4>
          <p className="text-slate-600 mb-8">Vi har modtaget din bookingforespørgsel og vender tilbage med en bekræftelse hurtigst muligt.</p>
          <button 
            onClick={() => setStatus(null)}
            className="bg-slate-900 text-white px-8 py-3 rounded-full font-bold transition-all active:scale-95"
          >
            Lav en ny booking
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Navn</label>
              <input 
                required 
                name="name"
                type="text" 
                placeholder="Dit fulde navn" 
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Telefon</label>
              <input 
                required 
                name="phone"
                type="tel" 
                placeholder="Dit telefonnummer" 
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 ml-1">E-mail</label>
            <input 
              required 
              name="email"
              type="email" 
              placeholder="din@mail.dk" 
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 ml-1">Bilmodel & Årgang</label>
            <input 
              name="car_model"
              type="text" 
              placeholder="F.eks. Tesla Model 3, 2023" 
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 ml-1">Ønsket dato for behandling</label>
            <input 
              name="preferred_date"
              type="date" 
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all text-slate-700"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 ml-1">Din besked</label>
            <textarea 
              name="message"
              rows="4" 
              placeholder="Fortæl os lidt om hvad du ønsker hjælp til..." 
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
            ></textarea>
          </div>

          <button 
            disabled={status === 'loading'}
            type="submit" 
            className="w-full bg-blue-900 hover:bg-blue-800 text-white font-black py-5 rounded-full text-lg shadow-xl shadow-blue-900/10 transition-all flex items-center justify-center gap-3 active:scale-[0.98] disabled:opacity-70"
          >
            {status === 'loading' ? 'Sender...' : 'Send bookingforespørgsel'}
            <Send size={20} />
          </button>
          
          {status === 'error' && (
            <p className="text-red-600 text-center font-bold text-sm">Der skete en fejl. Prøv venligst igen eller ring til os.</p>
          )}
        </form>
      )}
    </motion.div>
  );
}

export function SimpleContactForm() {
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <FormContent 
      status={status} 
      setStatus={setStatus} 
      handleSubmit={handleSubmit} 
    />
  );
}

export function GeneralContactForm() {
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-white rounded-[2.5rem] p-8 md:p-12 text-slate-900 shadow-2xl"
    >
      {status === 'success' ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={48} />
          </div>
          <h4 className="text-3xl font-black mb-4 uppercase">Tak for din besked!</h4>
          <p className="text-slate-600 mb-8">Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.</p>
          <button 
            onClick={() => setStatus(null)}
            className="bg-slate-900 text-white px-8 py-3 rounded-full font-bold transition-all active:scale-95"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">Navn</label>
              <input 
                required 
                name="name"
                type="text" 
                placeholder="Dit fulde navn" 
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 ml-1">E-mail</label>
              <input 
                required 
                name="email"
                type="email" 
                placeholder="din@mail.dk" 
                className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 ml-1">Emne</label>
            <input 
              required 
              name="subject"
              type="text" 
              placeholder="Hvad drejer din henvendelse sig om?" 
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 ml-1">Din besked</label>
            <textarea 
              required
              name="message"
              rows="6" 
              placeholder="Skriv din besked her..." 
              className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
            ></textarea>
          </div>

          <button 
            disabled={status === 'loading'}
            type="submit" 
            className="w-full bg-blue-900 hover:bg-blue-800 text-white font-black py-5 rounded-full text-lg shadow-xl shadow-blue-900/10 transition-all flex items-center justify-center gap-3 active:scale-[0.98] disabled:opacity-70"
          >
            {status === 'loading' ? 'Sender...' : 'Send besked'}
            <Send size={20} />
          </button>
          
          {status === 'error' && (
            <p className="text-red-600 text-center font-bold text-sm">Der skete en fejl. Prøv venligst igen eller ring til os.</p>
          )}
        </form>
      )}
    </motion.div>
  );
}

export default function ContactForm() {
  const [status, setStatus] = useState(null); // 'loading', 'success', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section id="kontakt" className="py-24 bg-slate-950 text-white overflow-hidden relative">
      {/* Background Image & Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/images/contact-bg.png" 
          alt="Car detailing background" 
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-16 items-start">
          
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:text-left text-center mx-auto lg:mx-0 w-full"
          >
            <h2 className="text-slate-500 font-bold tracking-widest uppercase text-sm mb-4">Booking</h2>
            <h3 className="text-4xl md:text-5xl font-black mb-8 leading-tight uppercase">
              Bestil tid til <br /><span className="text-slate-400">din bil</span>
            </h3>
            <p className="text-xl text-slate-400 mb-12 max-w-lg mx-auto lg:mx-0">
              Udfyld formularen for at booke en tid. Vi kontakter dig inden for 24 timer for at bekræfte aftalen.
            </p>

            <div className="grid sm:grid-cols-3 lg:grid-cols-1 gap-8 max-w-4xl mx-auto lg:mx-0">
              <div className="flex flex-col lg:flex-row items-center lg:items-center gap-4 lg:gap-6">
                <div className="w-14 h-14 rounded-2xl bg-white/5 border border-sky-400 flex items-center justify-center text-sky-400 shrink-0 shadow-[0_0_15px_rgba(51,195,247,0.2)]">
                  <Phone size={24} />
                </div>
                <div className="lg:text-left">
                  <p className="text-sm text-slate-500 font-bold uppercase tracking-wider">Ring til os</p>
                  <p className="text-lg md:text-xl font-bold">{companyInfo.phone}</p>
                </div>
              </div>
              
              <div className="flex flex-col lg:flex-row items-center lg:items-center gap-4 lg:gap-6">
                <div className="w-14 h-14 rounded-2xl bg-white/5 border border-sky-400 flex items-center justify-center text-sky-400 shrink-0 shadow-[0_0_15px_rgba(51,195,247,0.2)]">
                  <Mail size={24} />
                </div>
                <div className="lg:text-left">
                  <p className="text-sm text-slate-500 font-bold uppercase tracking-wider">Send en mail</p>
                  <p className="text-lg md:text-xl font-bold">{companyInfo.email}</p>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row items-center lg:items-center gap-4 lg:gap-6">
                <div className="w-14 h-14 rounded-2xl bg-white/5 border border-sky-400 flex items-center justify-center text-sky-400 shrink-0 shadow-[0_0_15px_rgba(51,195,247,0.2)]">
                  <MapPin size={24} />
                </div>
                <div className="lg:text-left">
                  <p className="text-sm text-slate-500 font-bold uppercase tracking-wider">Find os her</p>
                  <p className="text-lg md:text-xl font-bold">{companyInfo.address}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="w-full max-w-2xl mx-auto">
            <FormContent 
              status={status} 
              setStatus={setStatus} 
              handleSubmit={handleSubmit} 
            />
          </div>
        </div>
      </div>
    </section>
  );
}

